"use client";

import { useRef } from "react";
import type { Job } from "@/lib/api/types";
import type { OrgPersonResponse } from "@/lib/api/model/orgPersonResponse";
import { useJobListStore } from "@/features/jobs/store/job-store";
import { ArchiveConfirmModal } from "@/components/ui/archive-confirm-modal";
import {
  JobActionsMenu,
  JobActionsContextMenuContent,
} from "@/features/jobs/components/job-actions-menu";
import { TableRowContextMenu } from "@/components/ui/table-row-context-menu";
import { Icon } from "@/components/ui/icon";
import { Badge } from "@/components/ui/badge";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { AvatarNameBadge } from "@/components/ui/avatar-name-badge";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/date/format-short-date";
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyOverlay,
  TableFillerRow,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  useTableSkeletonCount,
} from "@/components/ui/table";
import { useJobRowActions } from "@/features/jobs/hooks/use-job-row-actions";
import { AtsBadge } from "@/features/jobs/components/ats-badge";
import { JobsSkeleton } from "./pending";

const COLUMN_COUNT = 6;

interface JobListTableProps {
  jobs: Job[];
  total: number;
  isLoading: boolean;
  hasFilters?: boolean;
  onClearFilters?: () => void;
}

const JOB_STATUS_BADGE: Record<
  string,
  {
    variant: "success" | "warning" | "outline-muted";
    label: string;
    className?: string;
  }
> = {
  active: {
    variant: "success",
    label: "Active",
    className: "border-border",
  },
  draft: {
    variant: "warning",
    label: "Draft",
    className: "border-border",
  },
  closed: {
    variant: "outline-muted",
    label: "Closed",
    className: "border-border bg-contrast/4 text-contrast/50",
  },
};

function JobStatusBadge({ status }: { status: string }) {
  const cfg = JOB_STATUS_BADGE[status] ?? JOB_STATUS_BADGE.closed;
  return (
    <Badge
      variant={cfg.variant}
      size="xs"
      className={cn("rounded-md leading-4", cfg.className)}
    >
      {cfg.label}
    </Badge>
  );
}

function JobsTHead({ sticky }: { sticky?: boolean }) {
  return (
    <TableHeader sticky={sticky}>
      <TableRow>
        <TableHead className="w-[25%] min-w-0">Name</TableHead>
        <TableHead className="w-[15%] min-w-0">Employer</TableHead>
        <TableHead className="w-[15%]">ATS Connection</TableHead>
        <TableHead className="w-[14%]">Created Date</TableHead>
        <TableHead className="w-[18%] min-w-0">Job Manager</TableHead>
        <TableHead className="w-[13%]">Status</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function JobListPagination({ total }: { total: number }) {
  const { page, perPage, setPage } = useJobListStore();
  return (
    <TablePagination
      page={page}
      total={total}
      perPage={perPage}
      onPageChange={setPage}
      label={(t) => `${t} job${t !== 1 ? "s" : ""}`}
    />
  );
}

function orgPersonDisplayName(m: OrgPersonResponse): string {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
  return name || m.email;
}

export function JobListTable({
  jobs,
  total,
  isLoading,
  hasFilters,
  onClearFilters,
}: JobListTableProps) {
  const loadingRef = useRef<HTMLDivElement>(null);
  const emptyRef = useRef<HTMLDivElement>(null);
  const skeletonCount = useTableSkeletonCount(loadingRef);
  const emptySkeletonCount = useTableSkeletonCount(emptyRef);
  const {
    openJob,
    cloneJob,
    cloningJobId,
    closeJob,
    reopenJob,
    requestArchive,
    confirmArchive,
    cancelArchive,
    archiveTarget,
    archivePending,
  } = useJobRowActions();

  if (isLoading) {
    return (
      <div
        ref={loadingRef}
        className="flex-1 flex flex-col bg-surface-primary overflow-hidden"
      >
        <Table variant="list" layout="fixed">
          <JobsTHead />
          <JobsSkeleton count={skeletonCount} />
        </Table>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <TableEmptyOverlay
        containerRef={emptyRef}
        icon="IconBag"
        title="No matching jobs"
        description="Try adjusting your filters or search query."
        action={
          hasFilters && onClearFilters ? (
            <TableEmptyOverlay.ClearFiltersButton onClick={onClearFilters} />
          ) : null
        }
        backdrop={
          <Table variant="list" layout="fixed">
            <JobsTHead />
            <JobsSkeleton count={emptySkeletonCount} />
          </Table>
        }
      />
    );
  }

  return (
    <>
      <div data-tour="jobs-table" className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden bg-surface-primary">
          <Table variant="list" layout="fixed">
            <JobsTHead sticky />
            <TableBody>
              {jobs.map((job) => {
                const jobHandlers = {
                  onView: () => openJob(job),
                  onClone: () => void cloneJob(job),
                  cloneDisabled: cloningJobId === job.id,
                  onClose: () => closeJob(job.id),
                  onReopen: () => reopenJob(job.id),
                  onArchive: () => requestArchive(job),
                };

                return (
                  <TableRowContextMenu
                    key={job.id}
                    content={
                      <JobActionsContextMenuContent job={job} {...jobHandlers} />
                    }
                  >
                    <TableRow onClick={() => openJob(job)}>
                      <TableCell className="min-w-0">
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex min-w-0 items-center gap-1">
                        <div className="shrink-0 opacity-50">
                          <Icon
                            name="IconBag"
                            size={14}
                            className="text-heading"
                          />
                        </div>
                        <span className="min-w-0 truncate text-sm text-contrast/80">
                          {job.title}
                        </span>
                      </div>
                      <div className="flex min-w-0 items-center gap-1">
                        <div className="shrink-0 opacity-50">
                          <Icon
                            name="IconPeopleCircle"
                            size={13}
                            className="text-heading"
                          />
                        </div>
                        <span className="min-w-0 truncate text-[13px] text-contrast/50">
                          {job.candidate_count}{" "}
                          {job.candidate_count === 1 ? "Profile" : "Profiles"}
                        </span>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="min-w-0">
                    <Badge
                      variant="outline-muted"
                      size="sm"
                      className="rounded-sm min-w-0 max-w-full shrink overflow-hidden"
                    >
                      <IconAvatar
                        src={job.employer.cover_url}
                        size={12}
                        alt={job.employer.name}
                        className="shrink-0 rounded-[2px]"
                        icon={
                          <Icon
                            name="IconBag"
                            className="text-heading opacity-50"
                          />
                        }
                      />
                      <span className="truncate">{job.employer.name}</span>
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <AtsBadge link={job.ats_link} />
                  </TableCell>

                  <TableCell>
                    <span className="text-sm text-contrast/80">
                      {formatShortDate(job.created_at)}
                    </span>
                  </TableCell>

                  <TableCell className="min-w-0">
                    {job.job_manager ? (
                      <AvatarNameBadge
                        id={job.job_manager.id}
                        name={orgPersonDisplayName(job.job_manager)}
                        avatarUrl={job.job_manager.avatar_url}
                        className="min-w-0 max-w-full shrink overflow-hidden [&>span]:truncate"
                      />
                    ) : (
                      <span className="text-sm text-contrast/40">
                        Unassigned
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="min-w-0">
                    <div className="flex min-w-0 items-center justify-between gap-4">
                      <JobStatusBadge status={job.status} />
                      <JobActionsMenu job={job} {...jobHandlers} />
                    </div>
                  </TableCell>
                    </TableRow>
                  </TableRowContextMenu>
                );
              })}
              <TableFillerRow columns={COLUMN_COUNT} />
            </TableBody>
          </Table>
        </div>

        <JobListPagination total={total} />
      </div>

      <ArchiveConfirmModal
        open={!!archiveTarget}
        onClose={cancelArchive}
        onConfirm={confirmArchive}
        entityType="job"
        entityName={archiveTarget?.title ?? ""}
        loading={archivePending}
        description="Archiving this job removes it from your active job list and pipeline views."
      />
    </>
  );
}
